import React, { useState, useEffect, useRef } from "react";
import { USE_MOCK_DATA, useAppContext } from "../../lib/mockState";
import { useT } from "../../lib/i18n";
import { Search, Scale, FileText, Users, Layers, ShieldCheck, X } from "lucide-react";

interface SearchResultItem {
  id: string;
  title: string;
  type: "CASE" | "CONTRACT" | "CLIENT" | "CLAUSE" | "EVIDENCE";
  subtype: string;
  status: string;
  snippetEn: string;
  snippetAr: string;
  jurisdiction: string;
  route: string;
}

const SEARCH_CORPUS: SearchResultItem[] = [
  {
    id: "CASE-2026-0042",
    title: "Smith v. Gulf Commercial Bank",
    type: "CASE",
    subtype: "Construction Delay dispute",
    status: "IN_HEARING",
    snippetEn: "Arbitration seat in DIFC. Claim value of AED 2.5 Million addressing concrete foundation issues.",
    snippetAr: "تحكيم أمام مركز دبي المالي العالمي. قيمة المطالبة 2.5 مليون درهم إماراتي بشأن معالجة الأساسات الخرسانية.",
    jurisdiction: "DIFC Courts",
    route: "/cases/CASE-2026-0042"
  },
  {
    id: "CON-2026-0117",
    title: "Rayzen Logistics Retainer MSA Agreement",
    type: "CONTRACT",
    subtype: "Technology SLA Provider",
    status: "DRAFT_CHECKPOINTS",
    snippetEn: "Bilateral agreement with 12 months term. Annual recurring value of AED 180,000.",
    snippetAr: "اتفاقية خدمات تقنية سنوية ثنائية بقيمة 180,000 درهم إماراتي تشتمل على بنود منظمة للسرية والملكية.",
    jurisdiction: "Abu Dhabi Global Market",
    route: "/contracts/CON-2026-0117"
  },
  {
    id: "RAYZEN",
    title: "Rayzen Logistics Limited",
    type: "CLIENT",
    subtype: "Active Corporate Account",
    status: "KYC_VALID",
    snippetEn: "Primary shipping logistics provider licensed in Abu Dhabi. AML Risk evaluated as High.",
    snippetAr: "مزود الشحن اللوجستي الرئيسي في أبوظبي. تقييم مخاطر غسل الأموال مصنف كفئة مرتفعة.",
    jurisdiction: "Abu Dhabi",
    route: "/clients"
  },
  {
    id: "CL-DIS-01",
    title: "Arbitration & DIFC Jurisdiction Seat Clause",
    type: "CLAUSE",
    subtype: "Boilerplate clause",
    status: "APPROVED_GUIDELINE",
    snippetEn: "Approved standard verbiage to route commercial disputes under LCIA Rules.",
    snippetAr: "صيغة قانونية معتمدة لإحالة النزاعات التجارية للتحكيم بموجب قواعد مركز دبي المالي.",
    jurisdiction: "DIFC",
    route: "/clause-library"
  },
  {
    id: "EVID-witness-mary",
    title: "Mary K. Witness Affidavit Log",
    type: "EVIDENCE",
    subtype: "Deposition Transcript",
    status: "VALIDATED_INTEGRITY",
    snippetEn: "Explicit testimony confirming delivery delays and unassigned subcontracts.",
    snippetAr: "شهادة مشفوعة بالقسم تؤكد التأخيرات الجسيمة في التسليم والتعاقدات الفرعية غير المصرح بها.",
    jurisdiction: "DIFC Courts",
    route: "/cases/CASE-2026-0042"
  }
];

export const GlobalSearch: React.FC = () => {
  const { lang, matters, clients, setCurrentRoute, setActiveMatterId } = useAppContext();
  const t = useT();
  const isAr = lang === "ar";
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const liveSearchCorpus: SearchResultItem[] = [
    ...matters.map((matter) => ({
      id: matter.matterId,
      title: matter.title,
      type: matter.matterSpecies,
      subtype: matter.lifecycle || matter.status || "Matter",
      status: matter.status || matter.lifecycle || "ACTIVE",
      snippetEn: matter.nextEvent || matter.clientName || "Live matter record",
      snippetAr: matter.nextEvent || matter.clientName || "سجل ملف مباشر",
      jurisdiction: matter.jurisdiction,
      route: matter.matterSpecies === "CASE" ? `/cases/${matter.matterId}` : `/contracts/${matter.matterId}`,
    })),
    ...clients.map((client) => ({
      id: client.clientId,
      title: client.name,
      type: "CLIENT" as const,
      subtype: client.type,
      status: client.status,
      snippetEn: client.industry || "Live client record",
      snippetAr: client.industry || "سجل عميل مباشر",
      jurisdiction: client.languages.join(", ") || "Firm client",
      route: "/clients",
    })),
  ];
  const searchCorpus = USE_MOCK_DATA ? SEARCH_CORPUS : liveSearchCorpus;

  const filteredResults = query.trim() === "" 
    ? [] 
    : searchCorpus.filter(item => {
        const q = query.toLowerCase();
        return (
          item.title.toLowerCase().includes(q) ||
          item.id.toLowerCase().includes(q) ||
          item.subtype.toLowerCase().includes(q) ||
          item.snippetEn.toLowerCase().includes(q) ||
          item.snippetAr.toLowerCase().includes(q) ||
          item.jurisdiction.toLowerCase().includes(q)
        );
      });

  const handleSelect = (item: SearchResultItem) => {
    setQuery("");
    setIsOpen(false);
    if (item.id.startsWith("CASE-") || item.id.startsWith("CON-")) {
      setActiveMatterId(item.id);
    }
    setCurrentRoute(item.route);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "CASE": return <Scale size={14} className="text-orange-500" />;
      case "CONTRACT": return <FileText size={14} className="text-purple-500" />;
      case "CLIENT": return <Users size={14} className="text-emerald-500" />;
      case "CLAUSE": return <Layers size={14} className="text-amber-500" />;
      default: return <ShieldCheck size={14} className="text-cyan-500" />;
    }
  };

  return (
    <div ref={containerRef} className="relative w-48 sm:w-64 md:w-80 font-sans" dir={isAr ? "rtl" : "ltr"}>
      <div className="relative">
        <div className={`absolute inset-y-0 flex items-center pointer-events-none px-3 text-slate-400 ${
          isAr ? "right-0" : "left-0"
        }`}>
          <Search size={14} />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={t("search.placeholder")}
          className={`w-full bg-slate-50 border border-slate-205 py-1.5 rounded-xl text-xs outline-none focus:bg-white focus:border-orange-500 focus:ring-1 focus:ring-orange-100/60 shadow-inner-xs transition-all ${
            isAr ? "pr-9 pl-3 text-right" : "pl-9 pr-3 text-left"
          }`}
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className={`absolute inset-y-0 flex items-center px-2.5 text-slate-350 hover:text-slate-600 ${
              isAr ? "left-0" : "right-0"
            }`}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {isOpen && query.trim() !== "" && (
        <div className="absolute top-full mt-2 w-72 sm:w-96 bg-white border border-slate-200 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.12)] z-55 overflow-hidden font-sans">
          <div className="bg-slate-50/70 p-2 border-b border-secondary/50 flex justify-between items-center px-4">
            <span className="text-[10px] font-mono font-bold uppercase text-slate-400 tracking-wider">
              {t("search.instantMatches")}
            </span>
            <span className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-mono font-semibold">
              {t("common.foundCount", { count: filteredResults.length })}
            </span>
          </div>

          <div className="max-h-72 overflow-y-auto custom-scrollbar divide-y divide-slate-100">
            {filteredResults.length > 0 ? (
              filteredResults.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className="p-3 hover:bg-slate-50/70 cursor-pointer transition-colors space-y-1 block text-left"
                >
                  <div className="flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {getIcon(item.type)}
                      <span className="text-xs font-black text-slate-900 truncate">
                        {item.title}
                      </span>
                    </div>
                    <span className="text-[9px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full uppercase font-bold shrink-0">
                      {item.id}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 line-clamp-1 leading-relaxed">
                    {isAr ? item.snippetAr : item.snippetEn}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    <span className="font-semibold">{item.subtype}</span>
                    <span>•</span>
                    <span className="font-mono text-[9px] uppercase">{item.jurisdiction}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-slate-400 text-xs">
                {t("search.noMatches")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
